import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Row, Select, Space, Statistic,
  Table, Tag, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import {
  ACCOUNT_TYPES, ACCOUNT_TYPE_LABEL, ACCOUNT_TYPE_COLOR, formatINR
} from './accountReportsConstants';
// Stage 6B.9H — friendly error / empty message helpers (consistent
// retry pattern across all report pages).
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Account Balance Summary (Stage 6B.5).
//
// Groups `ledger_accounts.current_balance` by account_type +
// account_group. Foundation for the future P&L and Balance Sheet
// reports — Stage 6B.5 ships grouped magnitudes + 5 type totals
// (assets, liabilities, income, expenses, equity).
//
// Sign-aware totals:
//   - assets / expenses → debit-positive → sum positive current_balance
//   - liabilities / income / equity → credit-positive → sum |negative|
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvSafe(c.title)).join(',');
  const body = rows.map((r) =>
    columns.map((c) => csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AccountBalancesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9H — preserve the last failure so the page can render an
  // Alert + Retry button instead of just flashing a toast.
  const [loadError, setLoadError] = useState(null);
  const [accountType, setAccountType] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await accountReportsApi.accountBalances({ accountType });
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'account_balances'));
    } finally { setLoading(false); }
  }, [accountType]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = data?.rows || [];
  const summary = data?.summary || {
    totalAssets: 0, totalLiabilities: 0, totalIncome: 0, totalExpenses: 0, totalEquity: 0
  };

  const columns = useMemo(() => ([
    { title: 'Account Type', dataIndex: 'accountType', key: 'accountType', width: 140,
      render: (v) => (
        <Tag color={ACCOUNT_TYPE_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {ACCOUNT_TYPE_LABEL[v] || v}
        </Tag>
      ) },
    { title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup',
      render: (v) => v || <Text type="secondary">(Ungrouped)</Text> },
    { title: 'Ledgers', dataIndex: 'ledgerCount', key: 'ledgerCount', width: 90, align: 'right' },
    { title: 'Debit (₹)', dataIndex: 'debitAmount', key: 'debitAmount', width: 140, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Credit (₹)', dataIndex: 'creditAmount', key: 'creditAmount', width: 140, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Net (₹)', dataIndex: 'netBalance', key: 'netBalance', width: 140, align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        if (n === 0) return <Text type="secondary">—</Text>;
        return (
          <span style={{ color: n > 0 ? '#3f8600' : '#cf1322' }}>
            {n > 0 ? '+' : ''}{formatINR(n)}
          </span>
        );
      } }
  ]), []);

  const csvColumns = useMemo(() => ([
    { title: 'Account Type', dataIndex: 'accountType' },
    { title: 'Group',        dataIndex: 'accountGroup' },
    { title: 'Ledgers',      dataIndex: 'ledgerCount' },
    { title: 'Debit',        dataIndex: 'debitAmount' },
    { title: 'Credit',       dataIndex: 'creditAmount' },
    { title: 'Net',          dataIndex: 'netBalance' }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={4} style={{ margin: 0 }}>Account Balances</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>Refresh</Button>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          <Button size="small" icon={<DownloadOutlined />} disabled={!rows.length}
            onClick={() => downloadCsv('account-balances.csv', rows, csvColumns)}>
            Export CSV
          </Button>
        </Space>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        Grouped magnitudes by account type and group, plus high-level totals for assets, liabilities, equity, income and expenses.
      </div>

      {loadError ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 10 }}
          message="Could not load Account Balances"
          description={friendlyAccountsErrorMessage(loadError, 'account_balances')}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
        <Space wrap>
          <Space>
            <Text>Filter by type:</Text>
            <Select size="small" allowClear placeholder="All types" style={{ width: 180 }}
              value={accountType} onChange={(v) => setAccountType(v || null)}
              options={ACCOUNT_TYPES.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABEL[t] }))} />
          </Space>
        </Space>
      </Card>

      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={12} md={8} xl={5}>
          <Card size="small"><Statistic title="Total Assets (₹)" value={summary.totalAssets}
            precision={2} valueStyle={{ color: '#1677ff' }} /></Card>
        </Col>
        <Col xs={12} md={8} xl={5}>
          <Card size="small"><Statistic title="Total Liabilities (₹)" value={summary.totalLiabilities}
            precision={2} valueStyle={{ color: '#c41d7f' }} /></Card>
        </Col>
        <Col xs={12} md={8} xl={5}>
          <Card size="small"><Statistic title="Total Equity (₹)" value={summary.totalEquity}
            precision={2} valueStyle={{ color: '#d48806' }} /></Card>
        </Col>
        <Col xs={12} md={8} xl={5}>
          <Card size="small"><Statistic title="Total Income (₹)" value={summary.totalIncome}
            precision={2} valueStyle={{ color: '#389e0d' }} /></Card>
        </Col>
        <Col xs={24} md={8} xl={4}>
          <Card size="small"><Statistic title="Total Expenses (₹)" value={summary.totalExpenses}
            precision={2} valueStyle={{ color: '#d4380d' }} /></Card>
        </Col>
      </Row>

      <Table
        size="small"
        rowKey={(r) => `${r.accountType}::${r.accountGroup || ''}`}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText:
          <Empty description={loading
            ? 'Loading…'
            : friendlyAccountsEmptyMessage('account_balances')} /> }}
      />
      </div>
    </ErpMasterShell>
  );
}
